import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { User, Client, Notification, Device, NotificationTemplate, NotificationChannel } from '../../models';
import { userApi } from '../../services/api';
import ConnectTemplateAutocomplete from '../../components/ConnectTemplateAutocomplete';
import ConnectChannelAutocomplete from '../../components/ConnectChannelAutocomplete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SendIcon from '@mui/icons-material/Send';

const UserDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [notificationDialog, setNotificationDialog] = useState(false);
  const [clientNotificationDialog, setClientNotificationDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<NotificationTemplate | null>(null);
  const [selectedChannels, setSelectedChannels] = useState<NotificationChannel[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [templateVariables, setTemplateVariables] = useState<Record<string, any>>({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    if (id) {
      fetchUserData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if(!id){
    return <Typography>User ID is required</Typography>;
  }

  const fetchUserData = async () => {
    try {
      const [userData, clientsData, notificationsData] = await Promise.all([
        userApi.getUser(id),
        userApi.getUserClients(id),
        userApi.getNotifications(id),
      ]);
      setUser(userData);
      // Sort clients by created_at (most recent first)
      const sortedClients = [...clientsData].sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setClients(sortedClients);
      setNotifications(notificationsData);
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendNotification = async () => {
    if (!user || !selectedTemplate || selectedChannels.length === 0) return;

    try {
      await userApi.sendNotification({
        user_id: user.id,
        template_id: selectedTemplate.id,
        channel_ids: selectedChannels.map(channel => channel.id),
        template_variables: templateVariables,
      });
      setNotificationDialog(false);
      setSelectedTemplate(null);
      setSelectedChannels([]);
      setTemplateVariables({});
      fetchUserData();
    } catch (error) {
      console.error('Error sending notification:', error);
    }
  };

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setSnackbarMessage('Copied to clipboard');
        setSnackbarOpen(true);
      })
      .catch((err) => console.error('Error copying text: ', err));
  };

  const handleClientNotification = (client: Client) => {
    setSelectedClient(client);
    setSelectedTemplate(null);
    setSelectedChannels([]);
    setTemplateVariables({});
    setClientNotificationDialog(true);
  };
  
  const handleSendClientNotification = async () => {
    if (!selectedClient || !selectedTemplate || selectedChannels.length === 0) return;

    try {
      await userApi.sendNotification({
        user_id: selectedClient.user.toString(),
        client_id: selectedClient.id,
        template_id: selectedTemplate.id,
        channel_ids: selectedChannels.map(channel => channel.id),
        template_variables: templateVariables,
      });
      setClientNotificationDialog(false);
      setSelectedClient(null);
      setSelectedTemplate(null);
      setSelectedChannels([]);
      setTemplateVariables({});
      fetchUserData();
      setSnackbarMessage('Notification sent successfully');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error sending notification:', error);
      setSnackbarMessage('Error sending notification');
      setSnackbarOpen(true);
    }
  };

  const clientColumns: GridColDef[] = [
    { field: 'platform', headerName: 'Platform', width: 100 },
    { field: 'version_name', headerName: 'Version', width: 100 },
    { 
      field: 'device',
      headerName: 'Device',
      width: 300,
      valueGetter: (value: Device) => `${value.model} (${value.brand})`,
    },
    {
      field: 'api_level',
      headerName: 'API Level',
      width: 100,
      valueGetter: (value, row) => row.device.api_level,
    },
    {
      field: 'fcm_token',
      headerName: 'FCM Token',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" noWrap sx={{ flexGrow: 1 }}>
            {params.value}
          </Typography>
          <Tooltip title="Copy to clipboard">
            <IconButton
              size="small"
              onClick={() => handleCopyToClipboard(params.value)}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'last_active',
      headerName: 'Last Active',
      width: 180,
      valueFormatter: (value) => value? new Date(value).toLocaleString() : '',
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'error'}
          size="small"
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 180,
      valueFormatter: (value) => value ? new Date(value).toLocaleString() : '',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex' }}>
          <Tooltip title="Send Push Notification">
            <IconButton
              size="small"
              onClick={() => handleClientNotification(params.row)}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const notificationColumns: GridColDef[] = [
    { field: 'title', headerName: 'Title', width: 200 },
    { field: 'body', headerName: 'Message', width: 300 },
    { 
      field: 'channel',
      headerName: 'Channel',
      width: 100
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={
            params.value === 'sent' ? 'success' :
            params.value === 'delivered' ? 'info' :
            params.value === 'read' ? 'primary' :
            params.value === 'failed' ? 'error' : 'warning'
          }
          size="small"
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 180,
      valueFormatter: (created_at) => new Date(created_at).toLocaleString(),
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" noWrap sx={{ flexGrow: 1 }}>
            {new Date(params.value).toLocaleString()}
          </Typography>
          <Tooltip title="Copy to clipboard">
            <IconButton
              size="small"
              onClick={() => handleCopyToClipboard(params.value)}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (loading || !user) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h5">User Details</Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setNotificationDialog(true)}
              >
                Connect with User
              </Button>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1">Name: {user.first_name} {user.last_name}</Typography>
                <Typography variant="subtitle1">Email: {user.email}</Typography>
                <Typography variant="subtitle1">Phone: {user.username || 'N/A'}</Typography>
                <Typography variant="subtitle1">WhatsApp: {user.username || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1">Status: {user.is_active ? 'Active' : 'Inactive'}</Typography>
                <Typography variant="subtitle1">Joined: {new Date(user.date_joined?? 0).toLocaleString()}</Typography>
                <Typography variant="subtitle1">Last Login: {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}</Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Connected Devices</Typography>
            <DataGrid
              rows={clients}
              columns={clientColumns}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 5, page: 0 },
                },
                sorting: {
                  sortModel: [{ field: 'created_at', sort: 'desc' }],
                },
              }}
              pageSizeOptions={[5]}
              disableRowSelectionOnClick
              autoHeight
            />
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Connect History</Typography>
            <DataGrid
              rows={notifications}
              columns={notificationColumns}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 5, page: 0 },
                },
              }}
              pageSizeOptions={[5]}
              disableRowSelectionOnClick
              autoHeight
            />
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={notificationDialog} onClose={() => setNotificationDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Connect with User</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Box sx={{ mb: 2 }}>
              <ConnectTemplateAutocomplete
                value={selectedTemplate}
                onChange={setSelectedTemplate}
                label="Connect Template"
                placeholder="Search for templates"
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <ConnectChannelAutocomplete
                value={selectedChannels}
                onChange={setSelectedChannels}
                label="Connect Channels"
                placeholder="Search for channels"
              />
            </Box>

            {selectedTemplate && selectedTemplate.variables.length > 0 && (
              <>
                <Typography variant="subtitle1" sx={{ mt: 2 }}>
                  Template Variables
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Template Variables (JSON)"
                  value={JSON.stringify(templateVariables, null, 2)}
                  onChange={(e) => {
                    try {
                      setTemplateVariables(JSON.parse(e.target.value));
                    } catch (error) {
                      // Invalid JSON, ignore
                    }
                  }}
                  sx={{ mb: 2 }}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotificationDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSendNotification}
            variant="contained"
            color="primary"
            disabled={!selectedTemplate || selectedChannels.length === 0}
          >
            Connect
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Client specific notification dialog */}
      <Dialog open={clientNotificationDialog} onClose={() => setClientNotificationDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Send Push Notification to Device
          {selectedClient && (
            <Typography variant="subtitle2" color="text.secondary">
              {selectedClient.device.model} ({selectedClient.platform})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Box sx={{ mb: 2 }}>
              <ConnectTemplateAutocomplete
                value={selectedTemplate}
                onChange={setSelectedTemplate}
                label="Connect Template"
                placeholder="Search for templates"
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <ConnectChannelAutocomplete
                value={selectedChannels}
                onChange={setSelectedChannels}
                label="Connect Channels"
                placeholder="Search for channels"
              />
            </Box>

            {selectedTemplate && selectedTemplate.variables.length > 0 && (
              <>
                <Typography variant="subtitle1" sx={{ mt: 2 }}>
                  Template Variables
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Template Variables (JSON)"
                  value={JSON.stringify(templateVariables, null, 2)}
                  onChange={(e) => {
                    try {
                      setTemplateVariables(JSON.parse(e.target.value));
                    } catch (error) {
                      // Invalid JSON, ignore
                    }
                  }}
                  sx={{ mb: 2 }}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClientNotificationDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSendClientNotification}
            variant="contained"
            color="primary"
            disabled={!selectedTemplate || selectedChannels.length === 0}
          >
            Send Notification
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserDetails;